import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, Receipt, TrendingUp, Send, Inbox, Radio, Percent } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";
import { MidStatsCard } from "@/components/payment/MidStats";

export default function PanelDashboard() {
    const nav = useNavigate();
    const [wallet, setWallet] = useState(null);
    const [ledger, setLedger] = useState([]);
    const [payouts, setPayouts] = useState([]);
    const [payinOrders, setPayinOrders] = useState([]);
    const [payinStats, setPayinStats] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [w, l, p, pi, ps] = await Promise.all([
                    api.get("/partner/payouts/wallet"),
                    api.get("/partner/payouts/ledger"),
                    api.get("/partner/payouts"),
                    api.get("/partner/payin/orders?limit=200").catch(() => ({ data: { items: [] }})),
                    api.get("/partner/payin/stats/today").catch(() => ({ data: null })),
                ]);
                setWallet(w.data);
                setLedger(l.data.items || []);
                setPayouts(p.data.items || []);
                setPayinOrders(pi.data.items || []);
                setPayinStats(ps.data);
            } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        })();
    }, []);

    const credits = ledger.filter((x) => x.direction === "credit");
    const debits = ledger.filter((x) => x.direction === "debit");
    const totalDeposits = credits.filter((x) => x.reason === "admin_topup").reduce((s, x) => s + x.amount, 0);
    const totalFees = 0;  // fees not charged on wallet credits in this build
    const totalPayouts = debits.filter((x) => x.reason === "payout").reduce((s, x) => s + x.amount, 0);
    const net = (wallet?.balance ?? 0);
    const used = totalDeposits > 0 ? (totalPayouts / totalDeposits) * 100 : 0;

    // Payin — today only
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const todayOrders = payinOrders.filter((o) => new Date(o.created_at) >= todayStart);
    const todayCaptured = todayOrders.filter((o) => o.status === "CAPTURED");
    const todayCapturedAmt = todayCaptured.reduce((s, o) => s + (o.amount || 0), 0);
    const payinSuccessRate = todayOrders.length ? (todayCaptured.length / todayOrders.length) * 100 : 0;

    const todayPayouts = payouts.filter((p) => new Date(p.created_at) >= todayStart);
    const todayPayoutSuccess = todayPayouts.filter((p) => p.status === "SUCCESS");
    const todayPayoutAmt = todayPayoutSuccess.reduce((s, p) => s + (p.amount || 0), 0);
    const payoutSuccessRate = todayPayouts.length ? (todayPayoutSuccess.length / todayPayouts.length) * 100 : 0;

    return (
        <div data-testid="panel-dashboard" className="p-6 space-y-6">
            {err && <div data-testid="panel-dashboard-err" className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            {/* Hero balance card */}
            <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-blue-950 text-white p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-300">Hi, {wallet?.email || "merchant"}</div>
                    <div className="text-sm text-slate-300 mt-1">Available to pay out</div>
                    <div data-testid="panel-balance" className="text-4xl sm:text-5xl font-bold mt-1 font-mono">{fmtINR(wallet?.balance ?? 0)}</div>
                </div>
                <div className="text-left sm:text-right w-full sm:w-auto">
                    <div className="text-[11px] uppercase tracking-widest text-slate-300">Used so far</div>
                    <div className="text-xl sm:text-2xl font-bold mt-1 font-mono text-amber-300">{fmtINR(totalPayouts)} <span className="text-xs sm:text-sm text-slate-400">of {fmtINR(totalDeposits)}</span></div>
                    <div className="w-full sm:w-56 h-1 bg-slate-700 rounded-full mt-3 overflow-hidden sm:ml-auto">
                        <div className="h-full bg-amber-400" style={{ width: `${Math.min(100, used)}%` }}></div>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">{used.toFixed(0)}% of received funds paid out</div>
                </div>
            </div>

            {/* Today — payin + payout tiles */}
            <div>
                <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Today</h3>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="dash-today-tiles">
                    <Card icon={ArrowDownLeft} label="Collected" value={fmtINR(payinStats?.captured_amount ?? todayCapturedAmt)}
                           sub={payinStats ? `${payinStats.captured_count} of ${payinStats.total_count} orders` : `${todayCaptured.length} of ${todayOrders.length} orders`}
                           tone="emerald" testid="card-today-payin" />
                    <Card icon={Percent} label="Commission today" value={fmtINR(payinStats?.commission_amount ?? 0)}
                           sub={payinStats && payinStats.captured_count > 0
                               ? `Net ${fmtINR(payinStats.net_amount)} credited`
                               : "No captures yet today"}
                           tone="rose" testid="card-today-commission" />
                    <Card icon={ArrowUpRight} label="Paid out" value={fmtINR(todayPayoutAmt)}
                           sub={`${todayPayoutSuccess.length} of ${todayPayouts.length} payouts`} tone="amber" testid="card-today-payout" />
                    <Card icon={TrendingUp} label="Collection success" value={`${(payinStats?.success_rate_pct ?? payinSuccessRate).toFixed(0)}%`}
                           sub={(payinStats?.total_count || todayOrders.length) ? `${payinStats?.captured_count ?? todayCaptured.length}/${payinStats?.total_count ?? todayOrders.length} captured` : "No orders yet"}
                           tone="slate" testid="card-today-payin-rate" />
                </div>
            </div>

            <MidStatsCard path="/partner/payin/mids/stats" title="Collections by MID" testid="partner-mid-stats" />

            {/* Account overview */}
            <div>
                <h2 className="font-bold text-xl">Account overview</h2>
                <p className="text-xs text-slate-500">Lifetime activity across every deposit &amp; payout</p>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mt-4">
                    <Card icon={ArrowDownLeft} label="Total Deposits" value={fmtINR(totalDeposits)}
                           sub={`${credits.length} payments received`} tone="slate" testid="card-deposits" />
                    <Card icon={Receipt} label="Per-Deposit Fee" value={`-${fmtINR(totalFees)}`}
                           sub="2.50% per deposit" tone="amber" testid="card-fees" />
                    <Card icon={ArrowUpRight} label="Total Payouts" value={fmtINR(totalPayouts)}
                           sub={`${payouts.length} payouts (no fee)`} tone="slate" testid="card-payouts" />
                    <Card icon={TrendingUp} label="Net Balance" value={fmtINR(net)}
                           sub="Credited − Paid out" tone="emerald" testid="card-net" />
                </div>
            </div>

            {/* Quick actions */}
            <div>
                <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Quick actions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <QuickAction testid="qa-send" icon={ArrowUpRight} label="Send Payout" onClick={() => nav("/partner/panel/send")} />
                    <QuickAction testid="qa-view" icon={Inbox} label="View Collections" onClick={() => nav("/partner/panel/funding")} />
                    <QuickAction testid="qa-api" icon={Radio} label="API & Webhooks" onClick={() => nav("/partner/panel/api")} />
                </div>
            </div>

            {/* Recent funding */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Recent funding</h3>
                    <button onClick={() => nav("/partner/panel/funding")}
                            className="text-xs text-blue-600 hover:underline">View all →</button>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                    {credits.length === 0 ? (
                        <p data-testid="recent-funding-empty" className="text-xs text-slate-400 text-center py-6">
                            No funding yet. Share your account details with payers to get started.
                        </p>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {credits.slice(0, 5).map((c) => (
                                <li key={c.entry_id} className="py-2 flex items-center justify-between text-xs">
                                    <div>
                                        <div className="font-medium">{c.note || c.reason}</div>
                                        <div className="text-[10px] text-slate-500 font-mono">{new Date(c.created_at).toLocaleString()}</div>
                                    </div>
                                    <div className="font-mono font-semibold text-emerald-700">+{fmtINR(c.amount)}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function Card({ icon: Icon, label, value, sub, tone, testid }) {
    const tones = {
        slate: "bg-white border-slate-200 text-slate-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
    };
    return (
        <div data-testid={testid} className={`${tones[tone]} border rounded-xl p-4`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <div className="text-2xl font-bold mt-1 font-mono">{value}</div>
            <div className="text-[10px] text-slate-500 mt-1">{sub}</div>
        </div>
    );
}

function QuickAction({ icon: Icon, label, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
                className="bg-white border border-slate-200 rounded-xl p-4 text-left hover:border-blue-300 hover:bg-blue-50 transition flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700">
                <Icon className="w-5 h-5" />
            </div>
            <div className="font-semibold text-sm">{label}</div>
        </button>
    );
}
